import axios from 'axios'

import { createStore } from '@thg-commerce/enterprise-cache'

const MORSE_BASE_URL =
  process.env.MORSE_BASE_URL || 'http://k8s.gb4.morse.io.thehut.local:8080'
const MORSE_PATH_OFFERS = '/v2/offers'
const MORSE_TIMEOUT = process.env.SPECIAL_OFFER_VARY_MORSE_TIMEOUT
  ? parseInt(process.env.SPECIAL_OFFER_VARY_MORSE_TIMEOUT, 10)
  : 5000

const SOR_BASE_URL =
  process.env.SOR_BASE_URL ||
  'http://gb4.searchorchestrationreader.io.thehut.local'
const SOR_PATH_SEARCH = '/search/search'
const SOR_PAGE_SIZE = 100000
const SOR_REQUEST_TIMEOUT = process.env.SPECIAL_OFFER_VARY_SOR_TIMEOUT
  ? parseInt(process.env.SPECIAL_OFFER_VARY_SOR_TIMEOUT, 10)
  : 3000

const PRODUCT_SELECTOR_ALL = 'all()'
const PRODUCT_SELECTOR_SINGLE = /^single\((\d+)\)$/
const PRODUCT_SELECTOR_DDL = /^ddl\((\d+)\)/
const PRODUCT_SELECTOR_LIST = /^list\((.*)\)$/

interface SpecialOffer {
  offer: number
  triggerClassName: string
  triggerParams: string
  calculatorParams: string
  startDate: number
  endDate: number
  isMarketed: boolean
  specialOfferMarketing: {} // We don't care about the contents, just of its existence
  changeOnsitePricing: boolean
}

interface SORRequest {
  ddl?: number
  listId?: number
  sectionId: number
  page: number
  limit: number
  offset: number
  channelName: string
  sitecode: string
  locale: string
  customerInfo: {
    currencyCode: string
    shippingDestinationCountryCode: string
  }
}

const listSKUStore = createStore<
  {
    ddl?: number
    listId?: number
    channel: string
    siteCode: string
    locale: string
    shippingDestination: string
    currency: string
  },
  number[]
>({
  key: (args) =>
    `list-skus-${args.channel}-${args.locale}-${
      args.ddl ? `ddl(${args.ddl})` : `list(${args.listId})`
    }`,
  ttlSeconds: 300,
  staleSeconds: Number.MAX_SAFE_INTEGER,
  forceBackground: true,
  lookup: async (args) => {
    if (!args.ddl && !args.listId) {
      console.warn('Invalid config for list sku lookup. No DDL or listID')
      return []
    }

    const request: SORRequest = {
      ddl: args.ddl || 0,
      listId: args.listId || 0,
      sectionId: 0,
      page: 1,
      limit: SOR_PAGE_SIZE,
      offset: 0,
      channelName: args.channel,
      sitecode: args.siteCode,
      locale: args.locale,
      customerInfo: {
        currencyCode: args.currency,
        shippingDestinationCountryCode: args.shippingDestination,
      },
    }
    const firstResponse = await axios.post<{
      searchResultProducts: { id: number }[]
      totalProducts: number
    }>(`${SOR_BASE_URL}${SOR_PATH_SEARCH}`, request, {
      timeout: SOR_REQUEST_TIMEOUT,
      headers: { 'X-THG-Client': 'Enterprise', 'user-agent': 'Enterprise' },
    })

    const skus = firstResponse.data.searchResultProducts?.map(
      (result) => result.id,
    )
    let productCount = firstResponse.data.totalProducts || 0

    while (skus.length < productCount) {
      const response = await axios.post<{
        searchResultProducts: { id: number }[]
        totalProducts: number
      }>(
        `${SOR_BASE_URL}${SOR_PATH_SEARCH}`,
        {
          ...request,
          offset: skus.length,
        },
        {
          headers: { 'X-THG-Client': 'Enterprise', 'user-agent': 'Enterprise' },
        },
      )

      productCount = response.data.totalProducts

      if (response.data.searchResultProducts.length === 0) {
        break
      }

      skus.push(
        ...response.data.searchResultProducts.map((result) => result.id),
      )
    }

    return skus
  },
})

const parseMorseParams = (params: string) => {
  return params
    .split(';')
    .reduce<{ [key: string]: string }>((accumulator, param) => {
      const [key, value] = param.split('=')
      accumulator[key] = value
      return accumulator
    }, {})
}

const setAddAll = <T>(set: Set<T>, values: T[]) => {
  for (const value of values) {
    set.add(value)
  }
}

type SpecialOfferSKUIndex = {
  [sku: string]: {
    customerListIds: Set<number> | number[]
    startDateTimestamp: number
    endDateTimestamp: number
  }
}

const specialOfferProductStore = createStore<
  {
    siteId: string
    locale: string
    channel: string
    siteCode: string
    shippingDestination: string
    currency: string
  },
  SpecialOfferSKUIndex
>({
  key: (args) => `special-offer-products-${args.siteId}-${args.locale}`,
  ttlSeconds: 300,
  staleSeconds: Number.MAX_SAFE_INTEGER,
  forceBackground: true,
  lookup: async (args) => {
    const response = await axios.get<SpecialOffer[]>(
      `${MORSE_BASE_URL}${MORSE_PATH_OFFERS}?siteId=${args.siteId}&locale=${args.locale}&marketable=true`,
      {
        timeout: MORSE_TIMEOUT,
        headers: { 'X-THG-Client': 'Enterprise', 'user-agent': 'Enterprise' },
      },
    )

    const customerListIds = await response.data.reduce<
      Promise<{
        [sku: string]: SpecialOfferSKUIndex['sku'] & {
          customerListIds: Set<number>
        }
      }>
    >(async (accumulatorPromise, offer) => {
      const accumulator = await accumulatorPromise

      const triggerParams = parseMorseParams(offer.triggerParams)
      const calculatorParams = parseMorseParams(offer.calculatorParams)
      if (
        (!triggerParams.qualprods &&
          !triggerParams.excludeprods &&
          !calculatorParams.qualprods &&
          !calculatorParams.excludeprods) ||
        !triggerParams.customerlists
      ) {
        return accumulator
      }

      if (!offer.changeOnsitePricing && !offer.specialOfferMarketing) {
        return accumulator
      }

      const productParam =
        triggerParams.qualprods ||
        triggerParams.excludeprods ||
        calculatorParams.qualprods ||
        calculatorParams.excludeprods
      if (!productParam) {
        return accumulator
      }

      const customerListIds = triggerParams.customerlists
        .split(',')
        .map((customerListId) => parseInt(customerListId, 10))

      if (productParam === PRODUCT_SELECTOR_ALL) {
        if (
          accumulator['*'] &&
          accumulator['*'].customerListIds instanceof Set
        ) {
          setAddAll(accumulator['*'].customerListIds, customerListIds)
        } else {
          accumulator['*'] = {
            customerListIds: new Set<number>(customerListIds),
            startDateTimestamp: offer.startDate,
            endDateTimestamp: offer.endDate,
          }
        }
        return accumulator
      }

      const singleMatch = PRODUCT_SELECTOR_SINGLE.exec(productParam)
      if (singleMatch && singleMatch[1]) {
        const sku = singleMatch[1]
        if (accumulator[sku]) {
          setAddAll(accumulator[sku].customerListIds, customerListIds)
        } else {
          accumulator[sku.toString()] = {
            customerListIds: new Set(customerListIds),
            startDateTimestamp: offer.startDate,
            endDateTimestamp: offer.endDate,
          }
        }
        return accumulator
      }

      const ddlMatch = PRODUCT_SELECTOR_DDL.exec(productParam)
      if (ddlMatch && ddlMatch[1]) {
        const ddl = parseInt(ddlMatch[1], 10)
        if (isNaN(ddl)) {
          return accumulator
        }

        const listSkus = await listSKUStore.get({
          ddl,
          channel: args.channel,
          siteCode: args.siteCode,
          locale: args.locale,
          shippingDestination: args.shippingDestination,
          currency: args.currency,
        })
        if (!listSkus) {
          return accumulator
        }

        listSkus.forEach((sku) => {
          if (accumulator[sku.toString()]) {
            setAddAll(
              accumulator[sku.toString()].customerListIds,
              customerListIds,
            )
          } else {
            accumulator[sku.toString()] = {
              customerListIds: new Set(customerListIds),
              startDateTimestamp: offer.startDate,
              endDateTimestamp: offer.endDate,
            }
          }
        })
        return accumulator
      }

      const listMatch = PRODUCT_SELECTOR_LIST.exec(productParam)
      if (listMatch && listMatch[1]) {
        const listId = parseInt(listMatch[1], 10)

        if (isNaN(listId)) {
          return accumulator
        }

        const listSkus = await listSKUStore.get({
          listId,
          channel: args.channel,
          siteCode: args.siteCode,
          locale: args.locale,
          shippingDestination: args.shippingDestination,
          currency: args.currency,
        })

        if (!listSkus) {
          return accumulator
        }

        listSkus.forEach((sku) => {
          if (accumulator[sku.toString()]) {
            setAddAll(
              accumulator[sku.toString()].customerListIds,
              customerListIds,
            )
          } else {
            accumulator[sku.toString()] = {
              customerListIds: new Set(customerListIds),
              startDateTimestamp: offer.startDate,
              endDateTimestamp: offer.endDate,
            }
          }
        })

        return accumulator
      }

      return accumulator
    }, Promise.resolve({}))

    return customerListIds
  },
})

export const specialOfferVary = async (request: {
  skus: number[]
  siteId: string
  channel: string
  siteCode: string
  locale: string
  shippingDestination: string
  currency: string
}) => {
  try {
    const specialOfferProducts = await specialOfferProductStore.get(request)
    if (!specialOfferProducts) {
      return []
    }

    const now = new Date().getTime()

    const headers = [...request.skus, '*'].reduce((accumulator, sku) => {
      if (!specialOfferProducts[sku]) {
        return accumulator
      }

      const specialOfferProduct = specialOfferProducts[sku]

      if (
        specialOfferProduct.startDateTimestamp > now ||
        specialOfferProduct.endDateTimestamp < now
      ) {
        return accumulator
      }

      const customerListIdArray =
        specialOfferProduct.customerListIds instanceof Set
          ? Array.from(specialOfferProduct.customerListIds)
          : specialOfferProduct.customerListIds

      customerListIdArray.forEach((customerListId) => {
        accumulator.add(`X-Customer-List-${customerListId}`)
      })

      return accumulator
    }, new Set<string>())

    const listHeaders = Array.from(headers)

    if (listHeaders.length > 0) {
      listHeaders.push('X-Customer-List')
    }

    return listHeaders
  } catch (e) {
    console.warn(
      `Failed to fetch special offer customer lists with error: ${e.message}`,
    )
    return []
  }
}
